import React, { useState } from 'react';
import { FmlxBanner, FmlxButton, FmlxTextBox } from 'fmlx-common-ui';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { TimePropertyDTO } from 'business/models/Models';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';
import { ARCHIVING_NAME_MAX_LENGTH } from '../constants';
import {
  ARCHIVING_NAME_VALIDATION_MESSAGES,
  ArchivingNameValidationError,
  formatArchivingScheduleTime,
  getArchivingNameValidationError,
} from '../utils';

const RESTORE_TARGET_EXPLORER_PATH = 'Projects >> Restored Archive Files';

type UnarchiveProjectModalBodyProps = {
  experimentCount: number;
  defaultProjectName?: string;
  onClose: () => void;
  onRunUnarchive: (projectName: string) => void;
};

// Collects the name of the new project that the selected experiments will be restored into.
// Duplicate-name validation is done server-side (a project with that name may already exist),
// so only required/invalid-character checks run client-side here. The field is pre-filled with
// defaultProjectName (the first selected experiment's name) but stays fully editable.
export const UnarchiveProjectModalBody = ({
  experimentCount,
  defaultProjectName = '',
  onClose,
  onRunUnarchive,
}: UnarchiveProjectModalBodyProps) => {
  const [projectName, setProjectName] = useState(defaultProjectName);
  const [validationError, setValidationError] =
    useState<ArchivingNameValidationError | null>(null);

  const scheduledStartTime = useSystemProperty<TimePropertyDTO>(
    PropertyConstants.ArchivingStartTime,
    '',
    { refreshOnUpdate: true, forceRefreshOnMount: true },
  );
  const scheduledEndTime = useSystemProperty<TimePropertyDTO>('Archiving.EndTime', '');

  const experimentLabel = experimentCount === 1 ? 'experiment' : 'experiments';
  const currentTimeString = new Date().toTimeString().slice(0, 8);
  const isWithinArchivingWindow =
    scheduledStartTime <= scheduledEndTime
      ? currentTimeString >= scheduledStartTime && currentTimeString <= scheduledEndTime
      : currentTimeString >= scheduledStartTime || currentTimeString <= scheduledEndTime;
  const experimentCountString =
    `${experimentCount} ${experimentLabel} will be restored to explorer ` +
    `(${RESTORE_TARGET_EXPLORER_PATH}).`;
  const restoreStartTimeText = isWithinArchivingWindow
    ? 'This process will start immediately.'
    : `This process will start around ${formatArchivingScheduleTime(scheduledStartTime)}`;
  const bannerTitle = `${experimentCountString} ${restoreStartTimeText}`;

  const handleRunUnarchive = () => {
    const error = getArchivingNameValidationError(projectName);
    if (error) {
      setValidationError(error);
      return;
    }

    onRunUnarchive(projectName.trim());
  };

  const handleNameChange = (value: string) => {
    setProjectName(value);
    if (validationError !== null) {
      setValidationError(getArchivingNameValidationError(value));
    }
  };

  const validationMessage = validationError
    ? ARCHIVING_NAME_VALIDATION_MESSAGES[validationError]
    : '';

  return (
    <div id="unarchive-project-modal-body" className="flex flex-col gap-4">
      <FmlxBanner
        id="unarchive-project-info-banner"
        variant={FmlxBanner.Variant.WARNING}
        title={bannerTitle}
        subtitle=""
        hideCloseButton
        show
      />

      <div id="unarchive-project-name-field" className="flex flex-col gap-1">
        <label
          id="unarchive-project-name-label"
          htmlFor="unarchive-project-name-input"
          className="text-sm text-gray-800"
        >
          Experiment will be restored to a project, please enter project name{' '}
          <span className="text-[#DB0000]">*</span>
        </label>
        <div>
          <FmlxTextBox
            id="unarchive-project-name-input"
            value={projectName}
            onChange={(event) => handleNameChange(event.value)}
            maxLength={ARCHIVING_NAME_MAX_LENGTH}
            variant={FmlxTextBox.Variant.OUTLINE}
          />
        </div>
        <div
          id="unarchive-project-name-error"
          className={`flex min-h-10 items-start gap-1 text-sm text-[#DB0000] ${
            validationError ? '' : 'invisible'
          }`}
          aria-hidden={!validationError}
        >
          <RMSvgIcon
            id="unarchive-project-name-error-icon"
            name="Error"
            fill="#DB0000"
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <span>{validationMessage}</span>
        </div>
        <div
          id="unarchive-project-name-counter"
          className="text-right text-xs text-gray-500"
        >
          {projectName.length}/{ARCHIVING_NAME_MAX_LENGTH}
        </div>
      </div>

      <div
        id="unarchive-project-modal-footer"
        className="flex flex-row items-center justify-end w-full h-12 gap-3 px-2 mt-2 border-t border-t-[#CACACA] border-solid border-0"
      >
        <FmlxButton
          id="unarchive-project-close-button"
          type={FmlxButton.Type.PRIMARY}
          variant={FmlxButton.Variant.OUTLINE}
          label="CLOSE"
          onClick={onClose}
        />
        <FmlxButton
          id="unarchive-project-run-button"
          type={FmlxButton.Type.PRIMARY}
          variant={FmlxButton.Variant.CONTAIN}
          label="CONFIRM"
          onClick={handleRunUnarchive}
        />
      </div>
    </div>
  );
};
